import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { showErrorToast } from '../../../../../shared/utils/apiErrorMessage'
import { useAuth } from '../../../../auth'
import {useGlobalFilter} from '../../../../../shared/context/GlobalFilterContext'
import {
  getCrewList,
  getSignOnOffRecords,
  getRanks,
  getVesselList,
  getCrewDashboardSummary,
  getCrewTrainings,
  getCrewAppraisals,
  getCrewComplaints,
  type CrewComplaint,
  getAllotmentPeriodRoster,
  type CrewDashboardSummary,
} from '../../../core/_requests'
import type { Crew } from '../../../core/_models'
import type { CrewDashboardData, CrewDerivedKpis } from '../types'
import { loadExpiryRows, loadAllotmentRows, loadRestActivity, monthRange, readExpiryCache, writeExpiryCache, clearExpiryCache, filterDashboardRows, type ExpiryRow } from '../crewDashboardData'
import { getCrewClaimsByMonth } from '../../../core/_requests'

export function useCrewDashboard() {
  const {currentUser, permissionsLoaded, resolvedPermissions, tenant, isSuperAdmin, hasPermission} = useAuth()
  const cacheIdentity = permissionsLoaded && currentUser?.id && (isSuperAdmin || hasPermission('READ_CREW_DOCUMENT'))
    ? JSON.stringify([currentUser.id, tenant, resolvedPermissions]) : ''
  const {state: globalState, vessels: globalVessels} = useGlobalFilter()
  const companyId = globalState.company?.id
  const companyType = globalState.company?.type
  const vesselId = globalState.vessel?.id
  const scopeKey = JSON.stringify([currentUser?.id, tenant, resolvedPermissions, companyType, companyId, vesselId])
  const filters = useMemo(() => ({
    ...(companyId ? companyType === 'company_admin' ? {companyAdminId: Number(companyId)} : {companyGroupId: Number(companyId)} : {}),
    ...(vesselId ? {vesselId: Number(vesselId)} : {}),
  }), [companyId, companyType, vesselId])
  const scope = useMemo(() => ({companyId, companyType, vesselId}), [companyId, companyType, vesselId])
  const [loadedScope, setLoadedScope] = useState('')
  const [scopeVessels, setScopeVessels] = useState<any[]>([])
  const currentMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` }
  const [claimsMonth, setClaimsMonth] = useState(currentMonth)
  const [allotmentMonth, setAllotmentMonth] = useState(currentMonth)
  const [restMonth, setRestMonth] = useState(currentMonth)
  const [loadedClaimsMonth, setLoadedClaimsMonth] = useState('')
  const [loadedAllotmentMonth, setLoadedAllotmentMonth] = useState('')
  const [loadedRestMonth, setLoadedRestMonth] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [crewList, setCrewList] = useState<Crew[]>([])
  const [signOnOffRecords, setSignOnOffRecords] = useState<any[]>([])
  const [ranks, setRanks] = useState<any[]>([])
  const [vessels, setVessels] = useState<any[]>([])
  const [trainings, setTrainings] = useState<any[]>([])
  const [appraisals, setAppraisals] = useState<any[]>([])
  const [complaints, setComplaints] = useState<CrewComplaint[]>([])
  const [expiry, setExpiry] = useState<ExpiryRow[] | null>(null)
  const [expiryError, setExpiryError] = useState(false)
  const [expiryProgress, setExpiryProgress] = useState({done: 0, total: 0})
  const [claims, setClaims] = useState<any[] | null>(null)
  const [allotments, setAllotments] = useState<any[] | null>(null)
  const [allotmentDenied, setAllotmentDenied] = useState(0)
  const [financeErrors, setFinanceErrors] = useState<string[]>([])
  const [restActivity, setRestActivity] = useState<any[] | null>(null)
  const [restError, setRestError] = useState(false)
  const expiryPending = useRef(false)
  const [failedSources, setFailedSources] = useState<string[]>([])
  const generation = useRef(0)
  const expiryController = useRef<AbortController | null>(null)

  const fetchAll = useCallback(async (force = false) => {
    const request = ++generation.current
    expiryController.current?.abort()
    const controller = new AbortController()
    expiryController.current = controller
    setLoading(true)
    setError(null)
    try {
      const results = await Promise.allSettled([
        getCrewList(filters),
        getSignOnOffRecords(),
        getRanks(),
        getVesselList(),
        getCrewTrainings(),
        getCrewAppraisals(),
        getCrewComplaints(),
      ])

      const [crewRes, signRes, rankRes, vesselRes, trainingRes, appraisalRes, complaintRes] = results
      if (request !== generation.current) return
      setFailedSources(results.flatMap((result, index) => result.status === 'rejected' ? [['crew', 'signOff', 'ranks', 'vessels', 'trainings', 'appraisals', 'complaints'][index]] : []))

      const metadata = [...globalVessels, ...(vesselRes.status === 'fulfilled' ? vesselRes.value || [] : [])]
      setScopeVessels(metadata)
      const crewRows = crewRes.status === 'fulfilled' ? crewRes.value || [] : []
      const visibleCrew = filterDashboardRows(crewRows, scope, metadata)
      const scoped = <T extends object>(rows: T[]) => filterDashboardRows(rows, scope, metadata, crewRows)
      setCrewList(visibleCrew)
      setSignOnOffRecords(signRes.status === 'fulfilled' ? scoped(signRes.value || []) : [])
      if (rankRes.status === 'fulfilled') setRanks(rankRes.value || [])
      setVessels(vesselRes.status === 'fulfilled' ? filterDashboardRows((vesselRes.value || []).map(v => ({...v, vesselId: v.id})), scope, metadata) : [])
      setTrainings(trainingRes.status === 'fulfilled' ? scoped(trainingRes.value || []) : [])
      setAppraisals(appraisalRes.status === 'fulfilled' ? scoped(appraisalRes.value || []) : [])
      setComplaints(complaintRes.status === 'fulfilled' ? scoped(complaintRes.value || []).sort((a, b) => b.id - a.id) : [])
      setLoadedScope(scopeKey)
      if (crewRes.status === 'fulfilled') {
        const scope = cacheIdentity ? JSON.stringify([cacheIdentity, scopeKey, visibleCrew.map(c => c.id).sort((a, b) => a - b)]) : ''
        const cached = !force && scope ? readExpiryCache(scope) : null
        setExpiry(cached)
        setExpiryError(false)
        setExpiryProgress({done: 0, total: visibleCrew.length})
        expiryPending.current = !cached
        if (!cached) loadExpiryRows(visibleCrew, controller.signal, (done, total) => {
          if (generation.current === request) setExpiryProgress({done, total})
        }).then(rows => {
          if (generation.current === request) { setExpiry(rows); expiryPending.current = false; if (scope) writeExpiryCache(scope, rows) }
        }).catch(() => {
          if (generation.current === request) { setExpiry(null); setExpiryError(true); expiryPending.current = false; clearExpiryCache() }
        })
      } else { setExpiry(null); setExpiryError(true); expiryPending.current = false; clearExpiryCache() }

      const rejected = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined
      if (rejected) {
        const msg = showErrorToast(rejected.reason, 'Failed to fetch some crew dashboard metrics.')
        setError(msg)
      }
    } catch (err) {
      const msg = showErrorToast(err, 'Failed to load crew dashboard data.')
      setError(msg)
    } finally {
      if (request === generation.current) setLoading(false)
    }
  }, [cacheIdentity, filters, scope, scopeKey, globalVessels])

  useEffect(() => {
    const currentGeneration = generation
    const currentController = expiryController
    fetchAll()
    const timer = window.setInterval(() => { if (document.visibilityState === 'visible' && !expiryPending.current) fetchAll() }, 300000)
    return () => { window.clearInterval(timer); currentGeneration.current++; currentController.current?.abort() }
  }, [fetchAll])

  useEffect(() => {
    if (loading || loadedScope !== scopeKey) return
    let active = true
    setLoadedClaimsMonth(`${scopeKey}:${claimsMonth}`); setClaims(null)
    setFinanceErrors(old => old.filter(source => source !== 'claims'))
    getCrewClaimsByMonth(monthRange(claimsMonth).start).then(rows => {
      if (!active) return
      if (!Array.isArray(rows)) throw new Error('Invalid claims response')
      setClaims(filterDashboardRows(rows, scope, scopeVessels, crewList))
    }).catch(() => { if (active) setFinanceErrors(old => [...old, 'claims']) })
    return () => { active = false }
  }, [claimsMonth, loading, loadedScope, scopeKey, scope, scopeVessels, crewList])

  useEffect(() => {
    if (loading || loadedScope !== scopeKey) return
    const controller = new AbortController()
    let active = true
    setLoadedAllotmentMonth(`${scopeKey}:${allotmentMonth}`); setAllotments(null); setAllotmentDenied(0)
    setFinanceErrors(old => old.filter(source => source !== 'allotments'))
    getAllotmentPeriodRoster(allotmentMonth, filters).then(rawRoster => {
      const roster = filterDashboardRows(rawRoster, scope, scopeVessels)
      if (!active) return null
      return loadAllotmentRows(roster.map(c => c.id), allotmentMonth, controller.signal).then(result => ({...result, rows: result.rows.map(row => ({...row, crewName: roster.find(c => c.id === row.crewId)?.name || '—'}))}))
    }).then(result => {
      if (active && result) { setAllotments(result.rows); setAllotmentDenied(result.denied) }
    }).catch(() => { if (active) setFinanceErrors(old => [...old, 'allotments']) })
    return () => { active = false; controller.abort() }
  }, [allotmentMonth, loading, loadedScope, scopeKey, filters, scope, scopeVessels])

  useEffect(() => {
    if (loading || loadedScope !== scopeKey) return
    const controller = new AbortController()
    let active = true
    setLoadedRestMonth(`${scopeKey}:${restMonth}`); setRestActivity(null); setRestError(false)
    const {start, end} = monthRange(restMonth)
    const restRequest = failedSources.includes('signOff') ? Promise.reject(new Error('Assignment data unavailable')) : loadRestActivity(signOnOffRecords, start, controller.signal, end)
    restRequest.then(rows => { if (active) setRestActivity(rows) }).catch(() => { if (active) setRestError(true) })
    return () => { active = false; controller.abort() }
  }, [restMonth, loading, loadedScope, scopeKey, signOnOffRecords, failedSources])

  const summary = useMemo<CrewDashboardSummary>(
    () => getCrewDashboardSummary(crewList, signOnOffRecords, ranks),
    [crewList, signOnOffRecords, ranks]
  )

  const data: CrewDashboardData = useMemo(() => ({
    crewList,
    signOnOffRecords,
    ranks,
    vessels,
    trainings,
    summary,
  }), [crewList, signOnOffRecords, ranks, vessels, trainings, summary])

  const kpis: CrewDerivedKpis = useMemo(() => ({
    totalCrew: summary.totalCrew,
    onboardNow: summary.onboardNow,
    pendingApprovals: summary.pendingApprovals,
    expiringCerts: expiry?.length ?? 0,
    signedOff: signOnOffRecords.filter((r: any) => r.status === 'SIGNED_OFF').length,
    vessels: vessels.length,
    pendingTraining: trainings.filter((t: any) => t.workflowStatus === 'SUBMITTED').length,
    pendingAppraisals: appraisals.filter((a: any) => a.workflowStatus === 'SUBMITTED').length,
  }), [summary, signOnOffRecords, vessels, trainings, appraisals, expiry])

  return {
    loading: loading || loadedScope !== scopeKey,
    error,
    data,
    kpis,
    summary,
    crewList,
    signOnOffRecords,
    ranks,
    vessels,
    trainings,
    appraisals,
    complaints,
    expiry,
    expiryError,
    expiryProgress,
    claimsMonth, setClaimsMonth,
    allotmentMonth, setAllotmentMonth,
    restMonth, setRestMonth,
    claims: loadedClaimsMonth === `${scopeKey}:${claimsMonth}` ? claims : null,
    allotments: loadedAllotmentMonth === `${scopeKey}:${allotmentMonth}` ? allotments : null,
    allotmentDenied: loadedAllotmentMonth === `${scopeKey}:${allotmentMonth}` ? allotmentDenied : 0,
    financeErrors: financeErrors.filter(source => source === 'claims' ? loadedClaimsMonth === `${scopeKey}:${claimsMonth}` : loadedAllotmentMonth === `${scopeKey}:${allotmentMonth}`),
    restActivity: loadedRestMonth === `${scopeKey}:${restMonth}` ? restActivity : null,
    restError: loadedRestMonth === `${scopeKey}:${restMonth}` ? restError : false,
    failedSources,
    refresh: () => fetchAll(true),
  }
}
